import { useState, useCallback, useRef, lazy, Suspense } from "react";
import { SmartHeader } from "@/components/SmartHeader";
import { HeroSection } from "@/components/HeroSection";
import { TelegramButton } from "@/components/TelegramButton";
import { ScrollReveal } from "@/hooks/useScrollReveal";

// Lazy load below-the-fold components to reduce initial JS/CSS bundle
const ImpactSection = lazy(() => import("@/components/ImpactSection").then(m => ({ default: m.ImpactSection })));
const ServicesSection = lazy(() => import("@/components/ServicesSection").then(m => ({ default: m.ServicesSection })));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection").then(m => ({ default: m.TestimonialsSection })));
const FAQSection = lazy(() => import("@/components/FAQSection").then(m => ({ default: m.FAQSection })));
const FooterSection = lazy(() => import("@/components/FooterSection").then(m => ({ default: m.FooterSection })));
const ReviewsOverlay = lazy(() => import("@/components/overlays/ReviewsOverlay").then(m => ({ default: m.ReviewsOverlay })));
const ServicesOverlay = lazy(() => import("@/components/overlays/ServicesOverlay").then(m => ({ default: m.ServicesOverlay })));
const ProjectOverlay = lazy(() => import("@/components/overlays/ProjectOverlay").then(m => ({ default: m.ProjectOverlay })));
const CaseOverlay = lazy(() => import("@/components/overlays/CaseOverlay").then(m => ({ default: m.CaseOverlay })));
const OverlayHeader = lazy(() => import("@/components/overlays/OverlayHeader").then(m => ({ default: m.OverlayHeader })));

type ActiveOverlay = 'reviews' | 'services' | 'project' | 'case' | null;

const Index = () => {
  const [activeOverlay, setActiveOverlay] = useState<ActiveOverlay>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const scrollPositionRef = useRef(0);

  const handleOpenReviews = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
    setActiveOverlay('reviews');
  }, []);

  const handleOpenServices = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
    setActiveOverlay('services');
  }, []);

  const handleOpenProject = useCallback((projectId: string) => {
    setSelectedProjectId(projectId);
    setActiveOverlay('project');
  }, []);

  const handleOpenCase = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
    setActiveOverlay('case');
  }, []);

  const handleCloseOverlay = useCallback(() => {
    setActiveOverlay(null);
    setSelectedProjectId(null);
    // Restore scroll position after overlay closes
    setTimeout(() => {
      window.scrollTo({ top: scrollPositionRef.current, behavior: 'instant' });
    }, 50);
  }, []);

  const handleBackFromProject = useCallback(() => {
    setActiveOverlay('reviews');
    setSelectedProjectId(null);
  }, []);

  const handleNavigate = useCallback((overlay: ActiveOverlay) => {
    if (overlay === null) {
      setActiveOverlay(null);
      setSelectedProjectId(null);
      // Always scroll to absolute top when going to main
      document.body.style.overflow = 'unset';
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      scrollPositionRef.current = window.scrollY;
      setActiveOverlay(overlay);
    }
  }, []);

  const scrollToFAQ = useCallback(() => {
    const faqSection = document.getElementById("faq");
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const isOverlayOpen = activeOverlay !== null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SmartHeader
        onOpenReviews={handleOpenReviews}
        onOpenServices={handleOpenServices}
        onScrollToFAQ={scrollToFAQ}
        isOverlayOpen={isOverlayOpen}
        onGoToMain={() => handleNavigate(null)}
      />
      
      {/* Section 1: Hero */}
      <HeroSection />
      
      {/* Section 2: Impact */}
      <div className="pt-16 md:pt-24 lg:pt-32">
        <ScrollReveal threshold={0.1}>
          <Suspense fallback={<div className="min-h-[600px]" />}>
            <ImpactSection onOpenCase={handleOpenCase} />
          </Suspense>
        </ScrollReveal>
      </div>
      
      {/* Section 3: Services */}
      <div className="pt-20 md:pt-28 lg:pt-36">
        <ScrollReveal threshold={0.1} delay={50}>
          <Suspense fallback={<div className="min-h-[500px]" />}>
            <ServicesSection />
          </Suspense>
        </ScrollReveal>
      </div>
      
      {/* Section 4: Testimonials */}
      <div className="pt-20 md:pt-28 lg:pt-36">
        <ScrollReveal threshold={0.1} delay={50}>
          <Suspense fallback={<div className="min-h-[400px]" />}>
            <TestimonialsSection />
          </Suspense>
        </ScrollReveal>
      </div>
      
      {/* Section 5: FAQ */}
      <div className="pt-20 md:pt-28 lg:pt-36">
        <ScrollReveal threshold={0.1} delay={50}>
          <Suspense fallback={<div className="min-h-[300px]" />}>
            <FAQSection />
          </Suspense>
        </ScrollReveal>
      </div>
      
      {/* Footer with Newsletter - flex-shrink-0 to stay at bottom */}
      <div className="pt-20 md:pt-28 lg:pt-32 mt-auto flex-shrink-0">
        <ScrollReveal threshold={0.05} delay={100}>
          <Suspense fallback={<div className="min-h-[200px]" />}>
            <FooterSection />
          </Suspense>
        </ScrollReveal>
      </div>
      
      <TelegramButton />

      {/* Overlays */}
      <Suspense fallback={null}>
        {activeOverlay && (
          <OverlayHeader 
            activeOverlay={activeOverlay}
            onClose={handleCloseOverlay}
            onBack={activeOverlay === 'project' ? handleBackFromProject : undefined}
            onNavigate={handleNavigate}
          />
        )}
        
        <ReviewsOverlay 
          isOpen={activeOverlay === 'reviews'}
          onClose={handleCloseOverlay}
          onOpenProject={handleOpenProject}
        />
        
        <ServicesOverlay 
          isOpen={activeOverlay === 'services'}
          onClose={handleCloseOverlay}
        />
        
        <ProjectOverlay 
          isOpen={activeOverlay === 'project'}
          projectId={selectedProjectId}
          onClose={handleCloseOverlay}
        />

        <CaseOverlay 
          isOpen={activeOverlay === 'case'}
          onClose={handleCloseOverlay}
        />
      </Suspense>
    </div>
  );
};

export default Index;
